import { env } from "@/config/env";
import { http, refreshOnce, setAccessToken, setRefreshHandler } from "./http";

/**
 * Sign-in and session.
 *
 * Partner admins are ordinary Curo users: the SAME Google/Apple OAuth as
 * everyone else. There is no separate credential system, and no internal secret
 * ever reaches the browser — authorization happens server-side in
 * requireOrgRole against the caller's own membership.
 *
 * The endpoint shapes below are the Gateway's actual ones (auth.route.ts), not
 * the tidier ones you might expect:
 *   - POST /api/auth/refresh returns tokens only, NOT the user.
 *   - GET  /api/auth/user is a separate call.
 *   - GET  /api/auth/logout is a GET, and it 302s to LOGOUT_URL.
 */

export interface SessionUser {
	id: string;
	email: string;
	name: string;
	image: string;
	has_access?: boolean;
	org_id?: string | null;
	org_slug?: string | null;
	org_role?: string | null;
}

export const authService = {
	/**
	 * Hand off to the Gateway's Google OAuth. `returnTo` must be an allowlisted
	 * origin (Gateway config/origins.ts) or the callback drops us on curohq.com
	 * instead — partner subdomains are matched by pattern, so no deploy is
	 * needed to add one.
	 */
	startGoogleSignIn(returnTo: string = window.location.href) {
		const url = new URL("/api/auth/google", env.apiBaseUrl);
		url.searchParams.set("returnTo", returnTo);
		window.location.assign(url.toString());
	},

	/**
	 * Exchange the httpOnly refresh cookie for an access token.
	 *
	 * Goes through refreshOnce, NOT straight to http.post. The Gateway rotates
	 * the refresh token on every call and revokes the whole family on reuse, so
	 * a second concurrent refresh does not just duplicate work — it signs the
	 * account out. Calling http.post directly here bypassed the single-flight
	 * guard entirely, which is how a page reload under React StrictMode logged
	 * people out: two boot effects, two refreshes, one replay, family revoked.
	 */
	async refresh(): Promise<boolean> {
		return refreshOnce();
	},

	/** The actual network call. Only refreshOnce should invoke this. */
	async performRefresh(): Promise<boolean> {
		try {
			const r = await http.post<{ accessToken: string }>("/api/auth/refresh");
			if (!r?.accessToken) return false;
			setAccessToken(r.accessToken);
			return true;
		} catch {
			setAccessToken(null);
			return false;
		}
	},

	/** The signed-in user. Separate call — /refresh does not return one. */
	async getUser(signal?: AbortSignal): Promise<SessionUser | null> {
		try {
			return await http.get<SessionUser>("/api/auth/user", { signal });
		} catch {
			return null;
		}
	},

	/**
	 * Set the signed-in person's display name.
	 *
	 * Email sign-ups arrive with no name — the local part of an address is not
	 * one, and guessing writes a fact nobody told us. This is how the real name
	 * gets supplied, and it is the same endpoint the mobile app uses after an
	 * Apple sign-up, which has the identical gap.
	 */
	async updateName(name: string): Promise<void> {
		await http.put("/api/user/name", { name });
	},

	/**
	 * Restore a session on boot: refresh, then read the user. Returns null when
	 * there is no valid session.
	 */
	async restore(): Promise<SessionUser | null> {
		if (!(await this.refresh())) return null;
		return this.getUser();
	},

	/**
	 * Ask for a sign-in code.
	 *
	 * Always resolves, and always says the same thing, because the Gateway
	 * answers identically whether or not the address has an account. Surfacing
	 * a difference here would rebuild the account-enumeration oracle the server
	 * deliberately refuses to be.
	 */
	async requestEmailCode(email: string): Promise<void> {
		await http.post("/api/auth/email/request", { email });
	},

	/**
	 * Redeem a code. Mints a session through the same path Google does, so a
	 * session is a session however it was obtained.
	 */
	async verifyEmailCode(email: string, code: string): Promise<SessionUser> {
		const r = await http.post<{ accessToken: string; user: SessionUser }>(
			"/api/auth/email/verify",
			{ email, code },
		);
		setAccessToken(r.accessToken);
		return r.user;
	},

	/**
	 * Sign out. The Gateway's /logout is a GET that redirects, so the response
	 * is not useful to us — clearing the local token is what matters, and it
	 * happens whether or not the call succeeds.
	 */
	async signOut(): Promise<void> {
		try {
			await fetch(new URL("/api/auth/logout", env.apiBaseUrl).toString(), {
				credentials: "include",
				redirect: "manual",
			});
		} catch {
			// Network failure must not strand someone in a signed-in-looking UI.
		} finally {
			setAccessToken(null);
		}
	},
};

// Let http retry a 401 by refreshing. Registered here rather than imported by
// http, which must not depend on a service — every service depends on http.
// The RAW call, deliberately — not authService.refresh(). refreshOnce invokes
// this handler, so pointing it back at refresh() would make refreshOnce await
// its own in-flight promise and hang forever on the first 401.
setRefreshHandler(() => authService.performRefresh());
